import { defineWidget } from '@sports/core'
import { LeagueTable, LEAGUE_TABLE_ATTRIBUTES, leagueTableStyles } from './widgets/league-table'
import { RaceCalendar, RACE_CALENDAR_ATTRIBUTES, raceCalendarStyles } from './widgets/race-calendar'
import { RaceCenter, RACE_CENTER_ATTRIBUTES, raceCenterStyles } from './widgets/race-center'
import { Racecards, RACECARDS_ATTRIBUTES, racecardsStyles } from './widgets/racecards'
import { Outright, OUTRIGHT_ATTRIBUTES, outrightStyles } from './widgets/outright'
import { Topscorer, TOPSCORER_ATTRIBUTES, topscorerStyles } from './widgets/topscorer'
import { MatchCenter, MATCH_CENTER_ATTRIBUTES, matchCenterStyles } from './widgets/match-center'
import { createGoalserveMatchCenter, GS_MATCH_CENTER_ATTRIBUTES } from './widgets/match-center/goalserve'
import { GolfLeaderboard, GOLF_LEADERBOARD_ATTRIBUTES, golfLeaderboardStyles } from './widgets/golf-leaderboard'
import { BoxingFights, BOXING_FIGHTS_ATTRIBUTES, boxingFightsStyles } from './widgets/boxing-fights'
import { MmaCards, MMA_CARDS_ATTRIBUTES, mmaCardsStyles } from './widgets/mma-cards'
import { DriverStandings, DRIVER_STANDINGS_ATTRIBUTES, driverStandingsStyles } from './widgets/driver-standings'
import { CricketCenter, CRICKET_CENTER_ATTRIBUTES, cricketCenterStyles } from './widgets/cricket-center'
import { CricketTable, CRICKET_TABLE_ATTRIBUTES, cricketTableStyles } from './widgets/cricket-table'

/**
 * Registering on import is what lets a host site embed with a single script
 * tag and then use the elements straight from markup — no init call, and
 * elements already in the DOM upgrade themselves when this runs.
 *
 * Adding a widget means one folder under `src/widgets/` and one entry here.
 */
defineWidget({
  tag: 'sports-league-table',
  component: LeagueTable,
  attributes: LEAGUE_TABLE_ATTRIBUTES,
  styles: leagueTableStyles,
})

defineWidget({
  tag: 'sports-race-calendar',
  component: RaceCalendar,
  attributes: RACE_CALENDAR_ATTRIBUTES,
  styles: raceCalendarStyles,
})

defineWidget({
  tag: 'sports-race-center',
  component: RaceCenter,
  attributes: RACE_CENTER_ATTRIBUTES,
  styles: raceCenterStyles,
})

defineWidget({
  tag: 'sports-racecards',
  component: Racecards,
  attributes: RACECARDS_ATTRIBUTES,
  styles: racecardsStyles,
})

defineWidget({
  tag: 'sports-outright',
  component: Outright,
  attributes: OUTRIGHT_ATTRIBUTES,
  styles: outrightStyles,
})

defineWidget({
  tag: 'sports-topscorer',
  component: Topscorer,
  attributes: TOPSCORER_ATTRIBUTES,
  styles: topscorerStyles,
})

defineWidget({
  tag: 'sports-golf-leaderboard',
  component: GolfLeaderboard,
  attributes: GOLF_LEADERBOARD_ATTRIBUTES,
  styles: golfLeaderboardStyles,
})

defineWidget({
  tag: 'sports-boxing-fights',
  component: BoxingFights,
  attributes: BOXING_FIGHTS_ATTRIBUTES,
  styles: boxingFightsStyles,
})

// MMA fight cards from mma-api (UFC, PFL…), styled like the match centres.
defineWidget({
  tag: 'sports-mma-cards',
  component: MmaCards,
  attributes: MMA_CARDS_ATTRIBUTES,
  styles: matchCenterStyles + mmaCardsStyles,
})

defineWidget({
  tag: 'sports-match-center',
  component: MatchCenter,
  attributes: MATCH_CENTER_ATTRIBUTES,
  styles: matchCenterStyles,
})

defineWidget({
  tag: 'sports-driver-standings',
  component: DriverStandings,
  attributes: DRIVER_STANDINGS_ATTRIBUTES,
  styles: driverStandingsStyles,
})

// Cricket is not a createGoalserveMatchCenter() sport: a match has innings, not
// a score, and its status is a sentence — so it has its own centre (same shell
// and controls) and a series points-table widget reading the cricket-standings
// store (there is no live `<id>_table` feed for cricket).
defineWidget({
  tag: 'sports-cricket-center',
  component: CricketCenter,
  attributes: CRICKET_CENTER_ATTRIBUTES,
  styles: cricketCenterStyles,
})

defineWidget({
  tag: 'sports-cricket-table',
  component: CricketTable,
  attributes: CRICKET_TABLE_ATTRIBUTES,
  styles: cricketTableStyles,
})

// The single match family: one Flashscore-shell "-center" per GoalServe team
// sport (matchCenterStyles). threeWay = a draw is possible (hockey/handball/
// rugby); logoSport = crest namespace (omitted where the feed has no crests,
// e.g. tennis players). This replaces the old per-sport "-matches" widgets.
const GS_CENTERS = [
  { tag: 'sports-soccer-center', sport: 'soccer', oddsCat: 'soccer_10', threeWay: true, logoSport: 'soccer', defaultHeading: 'Football' },
  { tag: 'sports-basketball-center', sport: 'basketball', oddsCat: 'basket_10', threeWay: false, logoSport: 'basketball', defaultHeading: 'Basketball' },
  { tag: 'sports-hockey-center', sport: 'hockey', oddsCat: 'hockey_10', threeWay: true, logoSport: 'hockey', defaultHeading: 'Ice Hockey', includeLeague: 'nhl|khl|iihf|world championship|olympic|champions hockey|stanley' },
  { tag: 'sports-nfl-center', sport: 'football', oddsCat: 'football_10', threeWay: false, logoSport: 'football', defaultHeading: 'NFL' },
  { tag: 'sports-baseball-center', sport: 'baseball', oddsCat: 'baseball_10', threeWay: false, logoSport: 'baseball', defaultHeading: 'Baseball' },
  { tag: 'sports-handball-center', sport: 'handball', oddsCat: 'handball_10', threeWay: true, logoSport: 'handball', defaultHeading: 'Handball', includeLeague: 'champions league|ehf|european league|asobal|bundesliga|starligue|lnh|handbold ligaen|world championship|olympic|world cup' },
  { tag: 'sports-volleyball-center', sport: 'volleyball', oddsCat: 'volleyball_10', threeWay: false, logoSport: 'volleyball', defaultHeading: 'Volleyball' },
  { tag: 'sports-rugby-center', sport: 'rugby', oddsCat: 'rugby_10', threeWay: true, logoSport: 'rugby', defaultHeading: 'Rugby' },
  { tag: 'sports-esports-center', sport: 'esports', oddsCat: 'esports_10', threeWay: false, logoSport: 'esports', defaultHeading: 'Esports' },
  // Rugby league: fixtures come from GoalServe's odds feed (the API rebuilds
  // the day from it). No crests — rugby-union crest ids don't cover it.
  { tag: 'sports-rugby-league-center', sport: 'rugby-league', oddsCat: 'rugbyleague_10', threeWay: true, logoSport: undefined, defaultHeading: 'Rugby League' },
  // Table tennis: GoalServe's feed is empty in our package, so the API serves
  // Kambi's fixtures and prices (Czech Liga Pro…) under its ids.
  { tag: 'sports-table-tennis-center', sport: 'table-tennis', oddsCat: 'kambi:table_tennis', threeWay: false, logoSport: undefined, defaultHeading: 'Table Tennis' },
  // Darts/snooker: player-vs-player knockouts, no draw, no crests.
  { tag: 'sports-darts-center', sport: 'darts', oddsCat: 'darts_10', threeWay: false, logoSport: undefined, defaultHeading: 'Darts' },
  { tag: 'sports-snooker-center', sport: 'snooker', oddsCat: 'snooker_10', threeWay: false, logoSport: undefined, defaultHeading: 'Snooker' },
  { tag: 'sports-tennis-center', sport: 'tennis', oddsCat: 'tennis_10', threeWay: false, logoSport: undefined, defaultHeading: 'Tennis', excludeLeague: 'doubles|qualification|boys|girls' },
] as const

for (const c of GS_CENTERS) {
  defineWidget({
    tag: c.tag,
    component: createGoalserveMatchCenter({ sport: c.sport, oddsCat: c.oddsCat, threeWay: c.threeWay, logoSport: c.logoSport, defaultHeading: c.defaultHeading, excludeLeague: (c as { excludeLeague?: string }).excludeLeague, includeLeague: (c as { includeLeague?: string }).includeLeague }),
    attributes: GS_MATCH_CENTER_ATTRIBUTES,
    styles: matchCenterStyles,
  })
}

// CMS-safe `<div data-sports-widget="…">` placeholders → real elements (see
// placeholders.ts). Must run after every defineWidget above.
import { watchPlaceholders } from './placeholders'
watchPlaceholders()

export { defineWidget }
